import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  UrlSegment,
} from '@angular/router';

import { Rights } from '@shared/enums';
import { SelectedSubproject } from '@shared/models';
import { UserDetailsFacade } from '@shared/state/facades';
import { Observable, combineLatest, map } from 'rxjs';
import { canActivateAccountGuard, canMatchAccountGuard } from './account.guard';
import { canActivateClientGuard, canMatchClientGuard } from './client.guard';
import {
  canActivateSubprojectGuard,
  canMatchSubprojectGuard,
} from './subproject.guard';
import { CanActivateFn, CanMatchFn } from '@shared/models/guard';

export const canActivateDprsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean> => {
  const router = inject(Router);
  const userDetailsFacade = inject(UserDetailsFacade);

  return combineLatest([
    canActivateAccountGuard(),
    canActivateClientGuard(),
    canActivateSubprojectGuard(route),
    userDetailsFacade.query.selectedSubproject$,
  ]).pipe(
    map(
      ([hasAccount, hasClient, hasSubprojectIdAndRights, selectedSubproject]) =>
        hasPermissions(
          router,
          hasAccount,
          hasClient,
          hasSubprojectIdAndRights,
          selectedSubproject
        )
    )
  );
};

export const canMatchDprsGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[]
): Observable<boolean> => {
  const router = inject(Router);
  const userDetailsFacade = inject(UserDetailsFacade);

  return combineLatest([
    canMatchAccountGuard(),
    canMatchClientGuard(),
    canMatchSubprojectGuard(route, segments),
    userDetailsFacade.query.selectedSubproject$,
  ]).pipe(
    map(
      ([hasAccount, hasClient, hasSubprojectIdAndRights, selectedSubproject]) =>
        hasPermissions(
          router,
          hasAccount,
          hasClient,
          hasSubprojectIdAndRights,
          selectedSubproject
        )
    )
  );
};

const hasPermissions = (
  router: Router,
  hasAccount: boolean,
  hasClient: boolean,
  hasSubprojectIdAndRights: boolean,
  selectedSubproject: SelectedSubproject
): boolean => {
  if (
    !hasAccount ||
    !hasClient ||
    !hasSubprojectIdAndRights ||
    selectedSubproject?.dprScreen === Rights.None
  ) {
    router.navigate(['/error'], {
      queryParams: {
        errorType: 'no-access-rights',
      },
    });

    return false;
  }

  return true;
};
